import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { CheckCircle2 } from "lucide-react";
import api, { apiError } from "../lib/api";
import { Money } from "../context/PrivacyContext";
import { fmtDate, todayISO } from "../lib/format";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

const addDays = (n) => {
  const d = new Date();
  d.setDate(d.getDate() + n);
  return d.toISOString().slice(0, 10);
};

const TYPE_LABELS = { fijo: "Fijo", variable: "Variable", estacional: "Estacional", programado: "Programado", financiacion: "Financiación", seguro: "Seguro", recurrente: "Recurrente" };

const FILTERS = [
  { key: "hoy", label: "Hoy", desde: () => todayISO(), hasta: () => todayISO() },
  { key: "7", label: "7 días", desde: () => todayISO(), hasta: () => addDays(7) },
  { key: "15", label: "15 días", desde: () => todayISO(), hasta: () => addDays(15) },
  { key: "30", label: "30 días", desde: () => todayISO(), hasta: () => addDays(30) },
  { key: "trimestre", label: "Trimestre", desde: () => todayISO(), hasta: () => addDays(90) },
  { key: "año", label: "Año", desde: () => todayISO(), hasta: () => addDays(365) },
];

export default function Pagos() {
  const [filter, setFilter] = useState("30");
  const [custom, setCustom] = useState({ desde: todayISO(), hasta: addDays(30) });
  const [showCustom, setShowCustom] = useState(false);
  const [data, setData] = useState(null);
  const [payItem, setPayItem] = useState(null);
  const [payForm, setPayForm] = useState({ paid_date: todayISO(), amount: "", account_id: null, method: "" });
  const [accounts, setAccounts] = useState([]);
  const [lastRange, setLastRange] = useState(null);
  const [view, setView] = useState("list");
  const [byAccount, setByAccount] = useState(null);

  const load = useCallback((desde, hasta) => {
    setData(null);
    setByAccount(null);
    setLastRange([desde, hasta]);
    api.get("/payments/pending", { params: { desde, hasta } }).then((r) => setData(r.data));
    api.get("/payments/by-account", { params: { desde, hasta } }).then((r) => setByAccount(r.data));
  }, []);

  useEffect(() => {
    api.get("/assets").then((r) => setAccounts(r.data));
  }, []);

  const openPay = (p) => {
    setPayItem(p);
    setPayForm({ paid_date: todayISO(), amount: String(p.amount), account_id: p.account_id || null, method: "" });
  };

  const confirmPay = async () => {
    try {
      await api.post("/payments/pay", {
        source_type: payItem.type,
        source_id: payItem.source_id || "",
        name: payItem.name,
        due_date: payItem.date,
        paid_date: payForm.paid_date,
        amount: parseFloat(String(payForm.amount).replace(",", ".")) || 0,
        account_id: payForm.account_id,
        method: payForm.method,
        category: payItem.category,
      });
      toast.success(`${payItem.name} marcado como pagado`);
      setPayItem(null);
      if (lastRange) load(lastRange[0], lastRange[1]);
    } catch (e) { toast.error(apiError(e)); }
  };

  useEffect(() => {
    const f = FILTERS.find((x) => x.key === filter);
    if (f) load(f.desde(), f.hasta());
  }, [filter, load]);

  return (
    <div className="space-y-6" data-testid="pagos-page">
      <div>
        <h1 className="font-heading text-3xl font-extrabold tracking-tighter">¿Cuánto me queda por pagar?</h1>
        <p className="text-sm text-muted-foreground mt-1">Gastos recurrentes y cuotas de financiación previstos.</p>
      </div>

      <div className="flex flex-wrap gap-2" data-testid="pagos-filters">
        {FILTERS.map((f) => (
          <button
            key={f.key}
            data-testid={`pagos-filter-${f.key}`}
            onClick={() => { setFilter(f.key); setShowCustom(false); }}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition-colors ${
              filter === f.key && !showCustom ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground hover:bg-secondary"
            }`}
          >
            {f.label}
          </button>
        ))}
        <button
          data-testid="pagos-filter-custom"
          onClick={() => setShowCustom(!showCustom)}
          className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition-colors ${
            showCustom ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground hover:bg-secondary"
          }`}
        >
          Rango de fechas
        </button>
      </div>

      {showCustom && (
        <div className="flex flex-wrap items-end gap-3 border border-border rounded-lg p-4">
          <div>
            <label className="text-xs text-muted-foreground">Desde</label>
            <Input data-testid="pagos-desde-input" type="date" value={custom.desde} onChange={(e) => setCustom({ ...custom, desde: e.target.value })} />
          </div>
          <div>
            <label className="text-xs text-muted-foreground">Hasta</label>
            <Input data-testid="pagos-hasta-input" type="date" value={custom.hasta} onChange={(e) => setCustom({ ...custom, hasta: e.target.value })} />
          </div>
          <Button data-testid="pagos-custom-apply" onClick={() => load(custom.desde, custom.hasta)}>Aplicar</Button>
        </div>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="border border-border rounded-lg p-4 sm:p-5 bg-primary text-primary-foreground">
          <div className="text-[10px] sm:text-xs uppercase tracking-[0.18em] font-bold text-white/60">Necesitarás</div>
          <Money value={data?.total ?? 0} testId="pagos-total" className="block mt-1 text-xl sm:text-3xl font-semibold tabular" />
        </div>
        <div className="border border-border rounded-lg p-4 sm:p-5">
          <div className="text-[10px] sm:text-xs uppercase tracking-[0.18em] text-muted-foreground font-bold">Disponible proyectado</div>
          <Money value={data?.disponible ?? 0} testId="pagos-disponible" className="block mt-1 text-xl sm:text-3xl font-semibold tabular" />
          <div className="text-[11px] text-muted-foreground mt-2 space-y-0.5 tabular">
            <div className="flex items-center justify-between">
              <span>Saldo actual</span>
              <Money value={data?.disponible_actual ?? 0} testId="pagos-saldo-actual" />
            </div>
            <div className="flex items-center justify-between text-emerald-700">
              <span>+ Ingresos previstos</span>
              <Money value={data?.ingresos_previstos ?? 0} testId="pagos-ingresos-previstos" />
            </div>
          </div>
        </div>
        <div className="border border-border rounded-lg p-4 sm:p-5">
          <div className="text-[10px] sm:text-xs uppercase tracking-[0.18em] text-muted-foreground font-bold">Ingresos previstos</div>
          <Money value={data?.ingresos_previstos ?? 0} className="block mt-1 text-xl sm:text-3xl font-semibold tabular text-emerald-700" />
          <div className="text-[11px] text-muted-foreground mt-2">{(data?.ingresos_previstos_items || []).length} cobro(s) pendiente(s) del rango. Excluye simulados y ya recibidos.</div>
        </div>
        <div className="border border-border rounded-lg p-4 sm:p-5">
          <div className="text-[10px] sm:text-xs uppercase tracking-[0.18em] text-muted-foreground font-bold">Déficit previsto</div>
          <Money value={data?.deficit ?? 0} testId="pagos-deficit" className={`block mt-1 text-xl sm:text-3xl font-semibold tabular ${(data?.deficit ?? 0) > 0 ? "text-red-600" : "text-emerald-600"}`} />
        </div>
      </div>

      <div className="flex gap-2" data-testid="pagos-view-tabs">
        {[["list", "Listado"], ["accounts", "Por cuenta"]].map(([k, l]) => (
          <button
            key={k}
            data-testid={`pagos-view-${k}`}
            onClick={() => setView(k)}
            className={`px-4 py-2 rounded-lg text-sm font-semibold border transition-colors ${view === k ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground hover:bg-secondary"}`}
          >
            {l}
          </button>
        ))}
      </div>

      {view === "accounts" ? (
        <div className="space-y-4" data-testid="pagos-by-account">
          <p className="text-sm text-muted-foreground">Cuánto dinero necesitas en cada cuenta para afrontar los vencimientos del periodo seleccionado. Asigna una «cuenta de cargo» a tus compromisos, financiaciones y seguros para verlos aquí.</p>
          {byAccount === null ? (
            <div className="p-5 text-sm text-muted-foreground border border-border rounded-lg">Calculando…</div>
          ) : byAccount.length === 0 ? (
            <div className="p-8 text-center text-sm text-muted-foreground border border-border rounded-lg">Sin datos para este periodo.</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {byAccount.map((b) => (
                <div key={b.account_id || "none"} className="border border-border rounded-lg p-5 space-y-3" data-testid={`account-flow-${b.account_id || "none"}`}>
                  <div className="flex items-start justify-between">
                    <div className="font-heading font-bold">{b.account_name}</div>
                    {b.deficit != null && b.deficit > 0 && (
                      <span className="text-[10px] font-bold uppercase tracking-wider bg-red-100 text-red-700 px-1.5 py-0.5 rounded">Déficit</span>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                    <div><div className="text-xs text-muted-foreground">Saldo actual</div>{b.balance != null ? <Money value={b.balance} className="font-semibold tabular" /> : <span className="text-muted-foreground">—</span>}</div>
                    <div><div className="text-xs text-muted-foreground">Necesitas ({b.upcoming_count} pagos)</div><Money value={b.upcoming_total} className="font-semibold tabular" /></div>
                    <div><div className="text-xs text-muted-foreground">Ya pagado en el periodo</div><Money value={b.paid_total} className="font-semibold tabular text-emerald-600" /></div>
                    {b.deficit != null && (
                      <div><div className="text-xs text-muted-foreground">Déficit previsto</div><Money value={b.deficit} className={`font-semibold tabular ${b.deficit > 0 ? "text-red-600" : "text-emerald-600"}`} /></div>
                    )}
                  </div>
                  {b.items.length > 0 && (
                    <div className="border-t border-border pt-2 space-y-1">
                      {b.items.slice(0, 5).map((it, i) => (
                        <div key={i} className="flex justify-between text-xs">
                          <span className="text-muted-foreground truncate">{fmtDate(it.date)} · {it.name}</span>
                          <Money value={it.amount} className="tabular font-semibold shrink-0" />
                        </div>
                      ))}
                      {b.items.length > 5 && <div className="text-[10px] text-muted-foreground">+ {b.items.length - 5} pagos más</div>}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      ) : (
      <div className="border border-border rounded-lg divide-y divide-border" data-testid="pagos-list">
        {data === null ? (
          <div className="p-5 text-sm text-muted-foreground">Calculando…</div>
        ) : data.items.length === 0 ? (
          <div className="p-8 text-center text-sm text-muted-foreground">No hay pagos previstos en este periodo.</div>
        ) : (
          data.items.map((p, i) => (
            <div key={i} className={`flex items-center justify-between p-4 gap-3 ${p.paid ? "opacity-50" : ""}`}>
              <div className="min-w-0">
                <div className="font-medium text-sm truncate flex items-center gap-1.5">
                  {p.name}
                  {p.paid && <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded"><CheckCircle2 size={10} /> Pagado</span>}
                </div>
                <div className="text-xs text-muted-foreground">{fmtDate(p.date)} · {TYPE_LABELS[p.type] || p.type} · {p.category}</div>
              </div>
              <div className="flex items-center gap-3 shrink-0">
                <Money value={p.amount} className={`font-semibold tabular ${p.paid ? "line-through" : ""}`} />
                {!p.paid && (
                  <Button data-testid={`pay-button-${i}`} size="sm" variant="outline" onClick={() => openPay(p)}>Marcar pagado</Button>
                )}
              </div>
            </div>
          ))
        )}
      </div>
      )}

      <Dialog open={!!payItem} onOpenChange={(v) => !v && setPayItem(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle className="font-heading">Marcar como pagado</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground -mt-2">{payItem?.name} · vencía el {fmtDate(payItem?.date)}. Se registrará como gasto real.</p>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-muted-foreground">Fecha real de pago</label>
                <Input data-testid="pay-date-input" type="date" value={payForm.paid_date} onChange={(e) => setPayForm({ ...payForm, paid_date: e.target.value })} />
              </div>
              <div>
                <label className="text-xs text-muted-foreground">Importe real (€)</label>
                <Input data-testid="pay-amount-input" inputMode="decimal" value={payForm.amount} onChange={(e) => setPayForm({ ...payForm, amount: e.target.value })} />
              </div>
            </div>
            <div>
              <label className="text-xs text-muted-foreground">Cuenta utilizada (opcional)</label>
              <Select value={payForm.account_id || "none"} onValueChange={(v) => setPayForm({ ...payForm, account_id: v === "none" ? null : v })}>
                <SelectTrigger data-testid="pay-account-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Sin especificar</SelectItem>
                  {accounts.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <Input data-testid="pay-method-input" placeholder="Forma de pago (ej. Domiciliación, Tarjeta)" value={payForm.method} onChange={(e) => setPayForm({ ...payForm, method: e.target.value })} />
            <Button data-testid="pay-confirm-button" onClick={confirmPay} disabled={!payForm.amount} className="w-full">Confirmar pago</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
